'use client'

import React from 'react'

import { Box, HTMLChakraProps, chakra } from '@chakra-ui/react'
import type { SystemStyleObject } from '@chakra-ui/react'

import { CloseIcon } from '../Icon'
import {
  ColorScheme,
  disabledTabStyles,
  tabCloseStyles,
  tabListStyles,
  tabStyles,
} from './Tabs.styles'

export type { ColorScheme }

export type TabItem = {
  id: string
  label: string
  icon?: React.ReactNode
  /**
   * Blocks selection of this tab. Use while work started on another tab is
   * still running, so the operator can't navigate away from it.
   */
  disabled?: boolean
  /**
   * Render a close (X) affordance on the tab; clicking it fires onTabClose.
   * Ignored while the tab is disabled — a tab the operator can't navigate away
   * from can't be closed either.
   */
  closable?: boolean
}

type ControlledButtonProps = 'onChange' | 'role' | 'type' | 'tabIndex'

export type TabsProps = Omit<
  HTMLChakraProps<'button'>,
  ControlledButtonProps
> & {
  items: TabItem[]
  activeId: string
  onChange: (id: string) => void
  /** Called when a closable tab's close affordance is activated. */
  onTabClose?: (id: string) => void
  /** Rendered after the tabs, inside the tablist — e.g. an "add tab" button. */
  trailingAction?: React.ReactNode
  colorScheme?: ColorScheme
  tabStyleOverrides?: SystemStyleObject
  tabListStyleOverrides?: SystemStyleObject
}

export const Tabs = ({
  items,
  activeId,
  onChange,
  onTabClose,
  trailingAction,
  colorScheme = 'dark',
  tabStyleOverrides,
  tabListStyleOverrides,
  ...rest
}: TabsProps) => {
  return (
    <Box
      role='tablist'
      {...tabListStyles(colorScheme)}
      {...tabListStyleOverrides}
    >
      {items.map((item) => {
        const isActive = item.id === activeId
        const isClosable = Boolean(
          item.closable && onTabClose && !item.disabled
        )
        return (
          <chakra.button
            aria-disabled={item.disabled}
            aria-keyshortcuts={isClosable ? 'Delete' : undefined}
            aria-selected={isActive}
            disabled={item.disabled}
            gap='4px'
            key={item.id}
            onClick={() => {
              if (!isActive && !item.disabled) onChange(item.id)
            }}
            // Closable tabs follow the WAI-ARIA pattern: the focused tab is
            // closed with Delete/Backspace. Keyboard users never need the
            // decorative close icon (which is mouse-only).
            onKeyDown={
              isClosable
                ? (e) => {
                    if (e.key === 'Delete' || e.key === 'Backspace') {
                      e.preventDefault()
                      onTabClose?.(item.id)
                    }
                  }
                : undefined
            }
            role='tab'
            tabIndex={isActive ? 0 : -1}
            type='button'
            {...tabStyles(isActive, colorScheme)}
            {...(item.disabled ? disabledTabStyles : {})}
            {...tabStyleOverrides}
            {...rest}
          >
            {item.label}
            {item.icon}
            {isClosable ? (
              // Decorative mouse affordance only: no interactive role/tabindex,
              // so it isn't a nested control in the tab's accessibility tree.
              // Keyboard closing is handled by the tab's Delete/Backspace above.
              <chakra.span
                aria-hidden='true'
                data-testid={`tab-close-${item.id}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onTabClose?.(item.id)
                }}
                // Keep the tab from taking focus when the close icon is pressed.
                onMouseDown={(e) => e.preventDefault()}
                {...tabCloseStyles(colorScheme)}
              >
                <CloseIcon colorScheme={colorScheme} size={12} />
              </chakra.span>
            ) : null}
          </chakra.button>
        )
      })}
      {trailingAction}
    </Box>
  )
}
